import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";

import type {
  CreateSupplierInventoryPriorityPayload,
  SupplierInventoryPriority,
  SupplierInventoryPriorityFilter,
  SupplierInventoryPriorityResponse,
} from "../types/supplier-inventory-priority.types";

type SupplierInventoryPriorityDetailResponse = {
  success: boolean;
  statusCode: number;
  message: string;
  resData: SupplierInventoryPriority;
};

type SupplierInventoryPriorityMutationResponse = {
  success: boolean;
  statusCode: number;
  message: string;
  resData: unknown;
};

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.supplierInventoryPriority;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const supplierInventoryPriorityService = {
  async list(
    filter: Partial<SupplierInventoryPriorityFilter>,
  ): Promise<SupplierInventoryPriorityResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<SupplierInventoryPriorityResponse>(
        endpoints.list(params.toString()),
        { method: "GET", isAuthenticated: true, cache: "no-store" },
      );
    } catch (error) {
      return failure(
        error,
        "Failed to load supplier inventory priorities due to an unexpected error.",
      ) as unknown as SupplierInventoryPriorityResponse;
    }
  },

  async details(id: number | string): Promise<SupplierInventoryPriorityDetailResponse> {
    try {
      return await apiClient<SupplierInventoryPriorityDetailResponse>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load supplier inventory priority details due to an unexpected error.",
      ) as unknown as SupplierInventoryPriorityDetailResponse;
    }
  },

  // only supplier_code is required, every other field is optional
  async create(
    payload: Pick<CreateSupplierInventoryPriorityPayload, "supplier_code"> &
      Partial<CreateSupplierInventoryPriorityPayload>,
  ): Promise<SupplierInventoryPriorityMutationResponse> {
    try {
      return await apiClient<SupplierInventoryPriorityMutationResponse>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to create supplier inventory priority due to an unexpected error.",
      ) as SupplierInventoryPriorityMutationResponse;
    }
  },

  async update(
    id: number | string,
    payload: Partial<CreateSupplierInventoryPriorityPayload>,
  ): Promise<SupplierInventoryPriorityMutationResponse> {
    try {
      return await apiClient<SupplierInventoryPriorityMutationResponse>(endpoints.update(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to update supplier inventory priority due to an unexpected error.",
      ) as SupplierInventoryPriorityMutationResponse;
    }
  },

  async updateStatus(
    id: number | string,
    payload: { status: "active" | "inactive" },
  ): Promise<SupplierInventoryPriorityMutationResponse> {
    return supplierInventoryPriorityService.update(id, payload);
  },

  async delete(id: number | string): Promise<SupplierInventoryPriorityMutationResponse> {
    try {
      return await apiClient<SupplierInventoryPriorityMutationResponse>(endpoints.delete(id), {
        method: "DELETE",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to delete supplier inventory priority due to an unexpected error.",
      ) as SupplierInventoryPriorityMutationResponse;
    }
  },
};
